var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { syntheticEvent } from '../../components/helpers';
import { positionStyle, getState, modifierClasses, fieldWidth, syncFieldError, escHtml, absPosition, } from './shared';
/** Native <button> — CSS-driven hover/active/focus */
export function syncNativeButton(node, parent, ctx) {
    var _a, _b, _c, _d, _e;
    var state = getState(node);
    var label = String((_a = state.label) !== null && _a !== void 0 ? _a : 'Button');
    var width = Number((_b = state.width) !== null && _b !== void 0 ? _b : 128);
    var height = Number((_c = state.height) !== null && _c !== void 0 ? _c : 40);
    var variant = String((_d = state.variant) !== null && _d !== void 0 ? _d : 'primary');
    var size = String((_e = state.size) !== null && _e !== void 0 ? _e : 'md');
    var disabled = Boolean(state.disabled);
    var el = ctx.nodeElements.get(node.id);
    if (!el) {
        el = document.createElement('button');
        el.type = 'button';
        el.id = node.id;
        parent.appendChild(el);
        el.addEventListener('click', function (e) {
            if (getState(node).disabled) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            node.emit('click', syntheticEvent('click', node));
            node.emit('change', syntheticEvent('change', node, { value: label }));
        });
        el.addEventListener('focus', function () { var _a; return (_a = node.getApp()) === null || _a === void 0 ? void 0 : _a.focusNode(node); });
        ctx.nodeElements.set(node.id, el);
    }
    else if (el.parentElement !== parent) {
        parent.appendChild(el);
    }
    el.className = "lightdraw-btn lightdraw-btn--".concat(variant).concat(size !== 'md' ? " lightdraw-btn--".concat(size) : '');
    el.textContent = label;
    el.disabled = disabled;
    el.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    el.style.cssText = positionStyle(node, width, height);
    ctx.applyA11y(node, el);
    ctx.applyUiClasses(node, el);
    ctx.seenIds.add(node.id);
}
/** Native checkbox with custom styled box */
export function syncNativeCheckbox(node, parent, ctx) {
    var _a, _b;
    var state = getState(node);
    var label = String((_a = state.label) !== null && _a !== void 0 ? _a : '');
    var checked = Boolean(state.checked);
    var disabled = Boolean(state.disabled);
    var size = String((_b = state.size) !== null && _b !== void 0 ? _b : 'md');
    var mods = { size: size, disabled: disabled };
    var wrap = ctx.nodeElements.get(node.id);
    if (!wrap) {
        wrap = document.createElement('label');
        wrap.id = node.id;
        wrap.innerHTML =
            '<input type="checkbox" class="lightdraw-checkbox-input" /><span class="lightdraw-checkbox-box" aria-hidden="true"></span><span class="lightdraw-checkbox-label"></span>';
        parent.appendChild(wrap);
        var input_1 = wrap.querySelector('input');
        input_1.addEventListener('change', function () {
            var _a;
            if (getState(node).disabled)
                return;
            var v = input_1.checked;
            node.metadata.componentState = __assign(__assign({}, getState(node)), { checked: v });
            node.ariaChecked = v;
            node.emit('change', syntheticEvent('change', node, { value: v }));
            (_a = node.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
        });
        input_1.addEventListener('focus', function () { var _a; return (_a = node.getApp()) === null || _a === void 0 ? void 0 : _a.focusNode(node); });
        ctx.nodeElements.set(node.id, wrap);
    }
    else if (wrap.parentElement !== parent) {
        parent.appendChild(wrap);
    }
    wrap.className = modifierClasses('lightdraw-checkbox', mods);
    var input = wrap.querySelector('input');
    var labelEl = wrap.querySelector('.lightdraw-checkbox-label');
    input.checked = checked;
    input.disabled = disabled;
    labelEl.textContent = label;
    wrap.style.cssText = positionStyle(node, Math.max(label.length * 8 + 36, 160), 24);
    ctx.applyA11y(node, wrap);
    ctx.applyUiClasses(node, wrap);
    if (node.focusable && !disabled)
        input.tabIndex = node.id === ctx.focusedNodeId ? 0 : -1;
    else
        input.removeAttribute('tabindex');
    ctx.seenIds.add(node.id);
}
/** Native text input with optional field label */
export function syncNativeInput(node, parent, ctx) {
    var _a, _b, _c, _d;
    var state = getState(node);
    var width = fieldWidth(state, 240);
    var value = String((_a = state.value) !== null && _a !== void 0 ? _a : '');
    var placeholder = String((_b = state.placeholder) !== null && _b !== void 0 ? _b : '');
    var label = String((_c = state.label) !== null && _c !== void 0 ? _c : '');
    var disabled = Boolean(state.disabled);
    var invalid = Boolean(state.invalid);
    var size = String((_d = state.size) !== null && _d !== void 0 ? _d : 'md');
    var error = state.error ? String(state.error) : '';
    var mods = { size: size, disabled: disabled, invalid: invalid, fullWidth: Boolean(state.fullWidth), error: error };
    var wrap = ctx.nodeElements.get(node.id);
    if (!wrap) {
        wrap = document.createElement('div');
        wrap.id = node.id;
        var input_2 = document.createElement('input');
        input_2.type = 'text';
        input_2.className = 'lightdraw-native-input';
        wrap.appendChild(input_2);
        parent.appendChild(wrap);
        input_2.addEventListener('input', function () {
            if (getState(node).disabled)
                return;
            var v = input_2.value;
            node.metadata.componentState = __assign(__assign({}, getState(node)), { value: v });
            node.emit('input', syntheticEvent('input', node, { value: v }));
        });
        input_2.addEventListener('change', function () {
            node.emit('change', syntheticEvent('change', node, { value: input_2.value }));
        });
        input_2.addEventListener('focus', function () { var _a; return (_a = node.getApp()) === null || _a === void 0 ? void 0 : _a.focusNode(node); });
        ctx.nodeElements.set(node.id, wrap);
    }
    else if (wrap.parentElement !== parent) {
        parent.appendChild(wrap);
    }
    wrap.className = modifierClasses('lightdraw-field', mods);
    var input = wrap.querySelector('input');
    if (label) {
        var labelEl = wrap.querySelector('.lightdraw-field-label');
        if (!labelEl) {
            labelEl = document.createElement('label');
            labelEl.className = 'lightdraw-field-label';
            wrap.insertBefore(labelEl, input);
        }
        labelEl.textContent = label;
        labelEl.setAttribute('for', "".concat(node.id, "-input"));
        input.id = "".concat(node.id, "-input");
    }
    input.value = value;
    input.placeholder = placeholder;
    input.disabled = disabled;
    input.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    syncFieldError(wrap, error || (invalid ? 'Invalid value' : ''));
    var fieldH = label ? (error || invalid ? 88 : 70) : 40;
    wrap.style.cssText = absPosition(node, width, fieldH);
    ctx.applyA11y(node, input);
    ctx.applyUiClasses(node, wrap);
    if (node.focusable && !disabled)
        input.tabIndex = node.id === ctx.focusedNodeId ? 0 : -1;
    else
        input.removeAttribute('tabindex');
    ctx.seenIds.add(node.id);
}
/** Native textarea with optional field label */
export function syncNativeTextarea(node, parent, ctx) {
    var _a, _b, _c, _d, _e;
    var state = getState(node);
    var width = fieldWidth(state, 280);
    var height = Number((_a = state.height) !== null && _a !== void 0 ? _a : 88);
    var value = String((_b = state.value) !== null && _b !== void 0 ? _b : '');
    var placeholder = String((_c = state.placeholder) !== null && _c !== void 0 ? _c : '');
    var label = String((_d = state.label) !== null && _d !== void 0 ? _d : '');
    var disabled = Boolean(state.disabled);
    var invalid = Boolean(state.invalid);
    var size = String((_e = state.size) !== null && _e !== void 0 ? _e : 'md');
    var error = state.error ? String(state.error) : '';
    var mods = { size: size, disabled: disabled, invalid: invalid, fullWidth: Boolean(state.fullWidth), error: error };
    var wrap = ctx.nodeElements.get(node.id);
    if (!wrap) {
        wrap = document.createElement('div');
        wrap.id = node.id;
        var ta_1 = document.createElement('textarea');
        ta_1.className = 'lightdraw-native-textarea';
        wrap.appendChild(ta_1);
        parent.appendChild(wrap);
        ta_1.addEventListener('input', function () {
            if (getState(node).disabled)
                return;
            node.metadata.componentState = __assign(__assign({}, getState(node)), { value: ta_1.value });
            node.emit('input', syntheticEvent('input', node, { value: ta_1.value }));
        });
        ta_1.addEventListener('change', function () {
            node.emit('change', syntheticEvent('change', node, { value: ta_1.value }));
        });
        ta_1.addEventListener('focus', function () { var _a; return (_a = node.getApp()) === null || _a === void 0 ? void 0 : _a.focusNode(node); });
        ctx.nodeElements.set(node.id, wrap);
    }
    else if (wrap.parentElement !== parent) {
        parent.appendChild(wrap);
    }
    wrap.className = modifierClasses('lightdraw-field lightdraw-field--textarea', mods);
    var ta = wrap.querySelector('textarea');
    if (label) {
        var labelEl = wrap.querySelector('.lightdraw-field-label');
        if (!labelEl) {
            labelEl = document.createElement('label');
            labelEl.className = 'lightdraw-field-label';
            wrap.insertBefore(labelEl, ta);
        }
        labelEl.textContent = label;
    }
    ta.value = value;
    ta.placeholder = placeholder;
    ta.disabled = disabled;
    ta.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    syncFieldError(wrap, error || (invalid ? 'Invalid value' : ''));
    ta.style.height = "".concat(height, "px");
    var errExtra = error || invalid ? 22 : 0;
    var fieldH = label ? height + 30 + errExtra : height + errExtra;
    wrap.style.cssText = absPosition(node, width, fieldH);
    ctx.applyA11y(node, ta);
    ctx.applyUiClasses(node, wrap);
    ctx.seenIds.add(node.id);
}
/** Native switch (checkbox + slider CSS) */
export function syncNativeToggle(node, parent, ctx) {
    var _a, _b;
    var state = getState(node);
    var on = Boolean(state.value);
    var label = String((_a = state.label) !== null && _a !== void 0 ? _a : '');
    var disabled = Boolean(state.disabled);
    var size = String((_b = state.size) !== null && _b !== void 0 ? _b : 'md');
    var mods = { size: size, disabled: disabled };
    var wrap = ctx.nodeElements.get(node.id);
    if (!wrap) {
        wrap = document.createElement('label');
        wrap.id = node.id;
        wrap.innerHTML =
            '<input type="checkbox" class="lightdraw-switch-input" role="switch" /><span class="lightdraw-switch-track" aria-hidden="true"><span class="lightdraw-switch-thumb"></span></span><span class="lightdraw-switch-label"></span>';
        parent.appendChild(wrap);
        var input_3 = wrap.querySelector('input');
        input_3.addEventListener('change', function () {
            var _a;
            if (getState(node).disabled)
                return;
            var v = input_3.checked;
            node.metadata.componentState = __assign(__assign({}, getState(node)), { value: v });
            node.ariaChecked = v;
            node.emit('change', syntheticEvent('change', node, { value: v }));
            (_a = node.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
        });
        input_3.addEventListener('focus', function () { var _a; return (_a = node.getApp()) === null || _a === void 0 ? void 0 : _a.focusNode(node); });
        ctx.nodeElements.set(node.id, wrap);
    }
    else if (wrap.parentElement !== parent) {
        parent.appendChild(wrap);
    }
    wrap.className = modifierClasses('lightdraw-switch-wrap', mods);
    var input = wrap.querySelector('input');
    var labelEl = wrap.querySelector('.lightdraw-switch-label');
    input.checked = on;
    input.disabled = disabled;
    labelEl.textContent = label;
    wrap.style.cssText = absPosition(node, Math.max(label.length * 8 + 80, 160), 28);
    ctx.applyA11y(node, wrap);
    ctx.applyUiClasses(node, wrap);
    ctx.seenIds.add(node.id);
}
/** Native range slider */
export function syncNativeSlider(node, parent, ctx) {
    var _a, _b, _c, _d, _e;
    var state = getState(node);
    var width = fieldWidth(state, 200);
    var min = Number((_a = state.min) !== null && _a !== void 0 ? _a : 0);
    var max = Number((_b = state.max) !== null && _b !== void 0 ? _b : 100);
    var value = Number((_c = state.value) !== null && _c !== void 0 ? _c : 50);
    var label = String((_d = state.label) !== null && _d !== void 0 ? _d : '');
    var disabled = Boolean(state.disabled);
    var size = String((_e = state.size) !== null && _e !== void 0 ? _e : 'md');
    var pct = ((value - min) / Math.max(max - min, 1)) * 100;
    var mods = { size: size, disabled: disabled };
    var wrap = ctx.nodeElements.get(node.id);
    if (!wrap) {
        wrap = document.createElement('div');
        wrap.id = node.id;
        wrap.innerHTML =
            '<div class="lightdraw-field-header"><span class="lightdraw-field-label"></span><span class="lightdraw-field-value"></span></div><input type="range" class="lightdraw-range" />';
        parent.appendChild(wrap);
        var input_4 = wrap.querySelector('input');
        input_4.addEventListener('input', function () {
            var _a;
            if (getState(node).disabled)
                return;
            var v = Number(input_4.value);
            node.metadata.componentState = __assign(__assign({}, getState(node)), { value: v });
            node.ariaValueNow = v;
            node.emit('input', syntheticEvent('input', node, { value: v }));
            (_a = node.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
        });
        input_4.addEventListener('change', function () {
            node.emit('change', syntheticEvent('change', node, { value: Number(input_4.value) }));
        });
        input_4.addEventListener('focus', function () { var _a; return (_a = node.getApp()) === null || _a === void 0 ? void 0 : _a.focusNode(node); });
        ctx.nodeElements.set(node.id, wrap);
    }
    else if (wrap.parentElement !== parent) {
        parent.appendChild(wrap);
    }
    wrap.className = modifierClasses('lightdraw-field lightdraw-field--slider', mods);
    var input = wrap.querySelector('input');
    var labelEl = wrap.querySelector('.lightdraw-field-label');
    var valueEl = wrap.querySelector('.lightdraw-field-value');
    labelEl.textContent = label || 'Value';
    valueEl.textContent = String(Math.round(value));
    input.min = String(min);
    input.max = String(max);
    input.value = String(value);
    input.disabled = disabled;
    node.ariaValueNow = value;
    node.ariaValueMin = min;
    node.ariaValueMax = max;
    wrap.style.cssText = "".concat(absPosition(node, width, 52), "--ld-range-pct:").concat(pct, "%;");
    ctx.applyA11y(node, input);
    ctx.applyUiClasses(node, wrap);
    ctx.seenIds.add(node.id);
}
/** Native radio */
export function syncNativeRadio(node, parent, ctx) {
    var _a, _b, _c;
    var state = getState(node);
    var label = String((_a = state.label) !== null && _a !== void 0 ? _a : '');
    var selected = Boolean(state.selected);
    var group = String((_b = state.group) !== null && _b !== void 0 ? _b : 'default');
    var disabled = Boolean(state.disabled);
    var size = String((_c = state.size) !== null && _c !== void 0 ? _c : 'md');
    var mods = { size: size, disabled: disabled };
    var wrap = ctx.nodeElements.get(node.id);
    if (!wrap) {
        wrap = document.createElement('label');
        wrap.id = node.id;
        wrap.innerHTML =
            '<input type="radio" class="lightdraw-radio-input" /><span class="lightdraw-radio-dot" aria-hidden="true"></span><span class="lightdraw-radio-label"></span>';
        parent.appendChild(wrap);
        var input_5 = wrap.querySelector('input');
        input_5.addEventListener('change', function () {
            var _a;
            if (getState(node).disabled)
                return;
            node.metadata.componentState = __assign(__assign({}, getState(node)), { selected: true });
            node.ariaChecked = true;
            node.emit('change', syntheticEvent('change', node, { value: group, payload: group }));
            (_a = node.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
        });
        input_5.addEventListener('focus', function () { var _a; return (_a = node.getApp()) === null || _a === void 0 ? void 0 : _a.focusNode(node); });
        ctx.nodeElements.set(node.id, wrap);
    }
    else if (wrap.parentElement !== parent) {
        parent.appendChild(wrap);
    }
    wrap.className = modifierClasses('lightdraw-radio', mods);
    var input = wrap.querySelector('input');
    var labelEl = wrap.querySelector('.lightdraw-radio-label');
    input.name = group;
    input.checked = selected;
    input.disabled = disabled;
    labelEl.textContent = label;
    wrap.style.cssText = positionStyle(node, Math.max(label.length * 8 + 32, 140), 22);
    ctx.applyA11y(node, wrap);
    ctx.applyUiClasses(node, wrap);
    ctx.seenIds.add(node.id);
}
/** CSS progress bar with optional label and variant */
export function syncNativeProgress(node, parent, ctx) {
    var _a, _b, _c, _d;
    var state = getState(node);
    var width = fieldWidth(state, 200);
    var value = Number((_a = state.value) !== null && _a !== void 0 ? _a : 0);
    var label = String((_b = state.label) !== null && _b !== void 0 ? _b : '');
    var variant = String((_c = state.variant) !== null && _c !== void 0 ? _c : 'default');
    var size = String((_d = state.size) !== null && _d !== void 0 ? _d : 'md');
    var disabled = Boolean(state.disabled);
    var mods = { size: size, disabled: disabled };
    var el = ctx.nodeElements.get(node.id);
    if (!el) {
        el = document.createElement('div');
        el.id = node.id;
        el.innerHTML =
            '<div class="lightdraw-progress-header"><span class="lightdraw-progress-label"></span><span class="lightdraw-progress-value"></span></div><div class="lightdraw-progress" role="progressbar"><div class="lightdraw-progress-bar"></div></div>';
        parent.appendChild(el);
        ctx.nodeElements.set(node.id, el);
    }
    else if (el.parentElement !== parent) {
        parent.appendChild(el);
    }
    el.className = modifierClasses('lightdraw-progress-wrap', mods);
    var track = el.querySelector('.lightdraw-progress');
    var bar = el.querySelector('.lightdraw-progress-bar');
    var labelEl = el.querySelector('.lightdraw-progress-label');
    var valueEl = el.querySelector('.lightdraw-progress-value');
    var pct = Math.max(0, Math.min(100, value));
    bar.style.width = "".concat(pct, "%");
    track.className = "lightdraw-progress".concat(variant !== 'default' ? " lightdraw-progress--".concat(variant) : '');
    el.setAttribute('role', 'progressbar');
    el.setAttribute('aria-valuenow', String(pct));
    el.setAttribute('aria-valuemin', '0');
    el.setAttribute('aria-valuemax', '100');
    if (disabled)
        el.setAttribute('aria-disabled', 'true');
    else
        el.removeAttribute('aria-disabled');
    var header = el.querySelector('.lightdraw-progress-header');
    if (label) {
        labelEl.textContent = label;
        valueEl.textContent = "".concat(Math.round(pct), "%");
        header.style.display = 'flex';
    }
    else {
        header.style.display = 'none';
    }
    var height = label ? 36 : 8;
    el.style.cssText = absPosition(node, width, height);
    ctx.applyUiClasses(node, el);
    ctx.seenIds.add(node.id);
}
/** Card shell — header band, subtitle, optional actions */
export function syncNativeCard(node, parent, ctx) {
    var _a, _b, _c;
    var state = getState(node);
    var width = Number((_a = state.width) !== null && _a !== void 0 ? _a : 280);
    var height = Number((_b = state.height) !== null && _b !== void 0 ? _b : 160);
    var title = state.title;
    var subtitle = state.subtitle ? String(state.subtitle) : '';
    var actions = (_c = state.actions) !== null && _c !== void 0 ? _c : [];
    var elevated = state.elevated !== false;
    var el = ctx.nodeElements.get(node.id);
    if (!el) {
        el = document.createElement('div');
        el.id = node.id;
        el.className = 'lightdraw-card';
        parent.appendChild(el);
        ctx.nodeElements.set(node.id, el);
    }
    else if (el.parentElement !== parent) {
        parent.appendChild(el);
    }
    el.className = "lightdraw-card".concat(elevated ? ' lightdraw-card--elevated' : '');
    var headerHtml = '';
    if (title || subtitle || actions.length) {
        var actionsHtml = actions
            .map(function (a) { return "<button type=\"button\" class=\"lightdraw-card-action\">".concat(escHtml(a), "</button>"); })
            .join('');
        headerHtml = "<div class=\"lightdraw-card-header\">\n      <div class=\"lightdraw-card-header-text\">\n        ".concat(title ? "<span class=\"lightdraw-card-title\">".concat(escHtml(String(title)), "</span>") : '', "\n        ").concat(subtitle ? "<span class=\"lightdraw-card-subtitle\">".concat(escHtml(subtitle), "</span>") : '', "\n      </div>\n      ").concat(actions.length ? "<div class=\"lightdraw-card-actions\">".concat(actionsHtml, "</div>") : '', "\n    </div>");
    }
    el.innerHTML = "".concat(headerHtml, "<div class=\"lightdraw-card-body\" aria-hidden=\"true\"></div>");
    el.style.cssText = positionStyle(node, width, height);
    ctx.applyUiClasses(node, el);
    ctx.seenIds.add(node.id);
}
